import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@restworld/feature-authentication';
import { isDev } from './use-is-dev-server';
import { Router } from 'next/router';
import posthog from 'posthog-js';
import type { PostHogConfig } from 'posthog-js';
import { usePostHog } from 'posthog-js/react';
import { IUBENDA_PURPOSE, onIubendaConsent } from '../utils/iubenda-consent';

// Campaign params worth carrying across the consent: the UTMs and the Google
// and Meta click ids that decide a visit's source family. A subset of the
// SDK's own CAMPAIGN_PARAMS (posthog-js `utils/event-utils`). Same list as the
// marketing site (website PostHogProvider.tsx), so both halves of a visit read
// the same way.
const CARRIED_CAMPAIGN_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gad_source',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
] as const;

/**
 * This page load's own entry: its first path and campaign params, read once
 * when PostHog is initialised (the same moment the SDK reads them) and kept in
 * memory only, never stored on the device.
 */
export function readVisitEntry(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const entry: Record<string, string> = { visit_landing_path: window.location.pathname };
  for (const key of CARRIED_CAMPAIGN_PARAMS) {
    const value = params.get(key);
    if (value) entry[key] = value;
  }
  return entry;
}

// Filled once, in the effect that calls posthog.init(). Module scope so a
// re-mount (HMR) keeps the entry of the page load, not of the re-mount.
let visitEntry: Record<string, string> = {};

export default function usePostHogProvider() {
  const { user, isAuthenticated } = useAuth();
  const posthogClient = usePostHog();

  const oldUrlRef = useRef('');
  // Tracks whether posthog.init() has finished. Without this, on-mount captures
  // (those fired from a child useEffect) race the parent provider's init useEffect
  // and get silently dropped — child effects run before parent effects.
  const [ready, setReady] = useState(false);
  // Whether the user granted the Iubenda "Measurement" purpose. Capture happens
  // regardless (anonymously without it — see cookieless_mode below); this only
  // gates identify()/group(), which attach personal data to the session.
  const [measurementConsent, setMeasurementConsent] = useState(false);

  useEffect(() => {
    // PostHog initialises unconditionally for every user, on every page.
    // Without Measurement consent (pending or rejected) `on_reject` keeps the
    // device clean — no cookies, no local/session storage — and users are
    // counted through PostHog's server-side daily-rotating anonymous hash
    // ("cookieless server hash mode", enabled in the project settings).
    // Consent, bridged from Iubenda below, switches to full persistent tracking.
    // Leaving cookieless mode the SDK resets (new distinct_id, device and
    // session): see the consent callback for what is carried across.
    if (posthog.__loaded) {
      // Re-mount with an already-initialised client (e.g. HMR): re-sync state.
      setReady(true);
      setMeasurementConsent(posthog.get_explicit_consent_status() === 'granted');
    } else {
      visitEntry = readVisitEntry();
      // `cookieless_mode` is implemented by the posthog-js runtime (1.399) but
      // missing from the public PostHogConfig type: typed via intersection so
      // every other key stays checked. Drop it once upstream adds the field.
      const config: Partial<PostHogConfig> & { cookieless_mode: 'on_reject' } = {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
        // Identity stitching with the marketing site (www.restworld.it, incl.
        // /lp/* landings) relies on the distinct_id living in a cookie on the
        // top-level domain (.restworld.it), readable by the proxied employer
        // app (/getting-started) too. These three values match today's
        // posthog-js runtime defaults on a custom domain AND the marketing
        // site's explicit config (website PostHogProvider.tsx) — set them
        // explicitly so a library bump can't silently change them and break
        // the stitch. `persistence` only takes effect for users who granted
        // Measurement consent: opted-out users stay in-memory + cookieless.
        persistence: 'localStorage+cookie',
        cross_subdomain_cookie: true,
        // No person profile for anonymous users until identify() is called.
        person_profiles: 'identified_only',
        cookieless_mode: 'on_reject',
        // With on_reject alone, "pending" users (banner ignored — the largest
        // lost segment) are NOT captured: cookieless capture only covers users
        // who are opted out. Defaulting to opt-out puts pending users in the
        // cookieless bucket too; persistence stays disabled for them, so
        // nothing is written to the device until they explicitly opt in.
        opt_out_capturing_by_default: true,
        loaded: (posthog) => {
          if (isDev) {
            posthog.debug();
            // Dev-only console handle for QA, e.g. forcing a flag variant:
            // posthog.featureFlags.overrideFeatureFlags({ flags: { 'ab-transparency-banner': 'banner' } })
            (window as unknown as Record<string, unknown>)['posthog'] = posthog;
          }
          setReady(true);
          // PostHog persists opt-in across sessions: a returning consenter is
          // already 'granted' here, before the Iubenda bridge re-fires.
          setMeasurementConsent(posthog.get_explicit_consent_status() === 'granted');
        },
        // What a replay is allowed to see. Who gets recorded is decided in the PostHog project
        // (replay trigger groups: employer at 100%, worker sampled); what a recording may
        // contain has to be decided here, next to the components that render it.
        // `maskAllInputs` is already the posthog-js default,
        // so everything the worker types is masked while they type it; this selector covers
        // the other half, the text once it is on screen. The interview with Serena is a
        // selection conversation: the point of recording it is where a candidate gets stuck,
        // never what they answered, and a replay that carries the answers is a copy of
        // personal data in a tool a dozen people can open. Elements marked
        // `data-ph-mask` render as blocks of asterisks in the replay and are unchanged in the
        // page. Nothing in the employer app carries the attribute, so nothing there changes.
        session_recording: { maskTextSelector: '[data-ph-mask]' },
        // Off, and meant to stay off on both apps. The project itself has autocapture enabled
        // (`autocapture_opt_out: false`), so this line is the only thing keeping every click,
        // input and form submit of every page out of our event count — on ~2M events a month,
        // already past the free million, that is the difference between a bill of tens of
        // dollars and one nobody budgeted. Asked for again on 22/09/2026 in #product, where
        // the answer to "can we turn autocapture on for the worker app" was no: named events
        // on the actions we care about instead, which is what the `worker_*` events are.
        autocapture: false,
        opt_in_site_apps: true,
      };
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY ?? '', config);
    }

    // Bridge Iubenda -> PostHog consent (Measurement purpose). Granted: full
    // tracking with device persistence. Denied or revoked: PostHog wipes its
    // storage and falls back to anonymous cookieless capture, so rejecting
    // users are still measured without being identifiable. Iubenda re-emits
    // the stored preference on every load (`onPreferenceExpressedOrNotNeeded`),
    // and `reloadOnConsent` reloads the page right after a banner choice.
    const unsubscribeConsent = onIubendaConsent(IUBENDA_PURPOSE.MEASUREMENT, (granted) => {
      setMeasurementConsent(granted);
      // Iubenda re-emits the stored preference on every page load
      // (`onPreferenceExpressedOrNotNeeded`): guard both directions so the
      // $opt_in event and the internal reset run on actual transitions only.
      if (granted) {
        if (!posthog.has_opted_in_capturing()) {
          // Last cookieless event of the visit: it marks the anonymous session
          // as continued in a consented one, so session-based funnels can leave
          // it out instead of counting the same visit twice. Sent at once
          // because the SDK resets right after.
          posthog.capture('consent_given', undefined, { send_instantly: true });
          // Leaving cookieless mode, opt_in_capturing() resets on purpose (new
          // distinct_id, new device, new session) and the campaign params the
          // SDK registered at init are gone. `$opt_in` is the first event of
          // the new session and PostHog builds the session's entry properties
          // (`$entry_utm_*`, `$entry_gclid`, ...) from it, so it carries this
          // page load's entry. No extra `$pageview` on purpose: the initial one
          // was already captured at init (cookieless) and the SDK does not
          // repeat it; Iubenda's `reloadOnConsent` then reloads the page, which
          // is its own pageview in the new session.
          posthog.opt_in_capturing({ captureProperties: visitEntry });
        }
      } else {
        if (!posthog.has_opted_out_capturing()) posthog.opt_out_capturing();
      }
    });

    oldUrlRef.current = window.location.href;

    // Pass the destination URL explicitly so shallow routing (e.g. /getting-started?step=X)
    // can't race PostHog's own window.location lookup and register the wrong step.
    const handleRouteChange = (url: string) => {
      if (!posthog.__loaded) return;
      const fullUrl = window.location.origin + url;
      posthog.capture('$pageview', { $current_url: fullUrl });
      oldUrlRef.current = fullUrl;
    };

    const handleRouteChangeStart = () => {
      if (!posthog.__loaded || !oldUrlRef.current) return;
      posthog.capture('$pageleave', {
        $current_url: oldUrlRef.current,
      });
    };

    Router.events.on('routeChangeComplete', handleRouteChange);
    Router.events.on('routeChangeStart', handleRouteChangeStart);

    return () => {
      unsubscribeConsent();
      Router.events.off('routeChangeComplete', handleRouteChange);
      Router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    // identify()/group() attach personal data (email, name, phone) and persist
    // a distinct_id on the device, so they require the Measurement purpose.
    // Logged-in users without consent (e.g. the whole /getting-started flow,
    // where the banner is intentionally not shown) stay anonymous-cookieless.
    if (!posthog.__loaded || !measurementConsent) return;
    posthogClient?.identify(user.id, {
      email: user.email,
      name: user.name,
      first_name: user.name,
      last_name: user.surname,
      created: user.inserted_at,
      avatar: user.picture,
      type: user.type,
      phone: user.phone_number,
      organization_source: user.organization_source,
      using_ppu_business_model: user?.using_ppu_business_model,
    });

    if (user.organization_id) {
      posthogClient?.group('organization', user.organization_id, {
        name: user.business_name,
        using_ppu_business_model: user.using_ppu_business_model,
      });
    }
  }, [isAuthenticated, user, posthogClient, measurementConsent]);

  const trackEvent = useCallback(
    (eventName: string, eventProperties?: Record<string, unknown>) => {
      if (!posthogClient || !ready) return;
      posthogClient.capture(eventName, eventProperties);
    },
    [posthogClient, ready]
  );

  return { trackEvent };
}
